import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Sicherheitsnetz fuer den Ladeschleier: meldet ein Bild nie `load` – abgebrochene
 * Verbindung, Fehler aus dem Cache –, muss die Buehne trotzdem wieder freigegeben
 * werden, sonst bliebe die Galerie dauerhaft verdeckt.
 */
const REVEAL_TIMEOUT_MS = 4000;

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Beim Variantenwechsel wird die Galerie komplett ausgetauscht; der
    // Ladezustand kommt am neuen Element als Attribut an. Erst hier steht es im
    // Dokument und kann auf seine Bilder warten.
    if (this.hasAttribute('data-loading')) this.#revealWhenReady();
  }

  #controller = new AbortController();

  /** Laufende Nummer der juengsten Variantenanfrage. */
  #request = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Sofort, nicht erst wenn die neue Section da ist: zwischen Klick auf einen
    // Farbtupfer und der Antwort liegt der Netzweg, und genau in dieser Zeit
    // stand bisher unveraendert das alte Bild – der Klick wirkte folgenlos.
    this.setAttribute('data-loading', '');

    // Bei zwei schnellen Klicks bricht die erste Anfrage ab und landet im
    // catch – ohne diese Nummer naehme sie dort den Schleier weg, obwohl die
    // zweite noch laeuft, und er blitzte kurz auf und wieder an.
    const request = ++this.#request;

    event.promise
      .then(({ detail }) => {
        const newMediaGallery = detail?.html?.querySelector('media-gallery');

        if (!newMediaGallery) {
          if (request === this.#request) this.removeAttribute('data-loading');
          return;
        }

        // Der Zustand wandert mit auf das neue Element: ohne ihn blitzte
        // zwischen Austausch und fertig geladenem Bild die leere Buehne auf.
        newMediaGallery.setAttribute('data-loading', '');
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (request === this.#request) this.removeAttribute('data-loading');
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Nimmt den Ladeschleier weg, sobald die zuerst sichtbaren Bilder da sind.
   *
   * Nur die ersten beiden Buehnenbilder werden abgewartet: mehr ist im Raster
   * nicht zu sehen, und im Slider laedt der Rest ohnehin erst beim Blaettern.
   * Auf sie zu warten wuerde den Schleier unnoetig lange stehen lassen.
   */
  async #revealWhenReady() {
    const pending = [...this.querySelectorAll('.product-media-container')]
      .slice(0, 2)
      .flatMap((container) => /** @type {HTMLImageElement[]} */ ([...container.querySelectorAll('img')]))
      .filter((image) => !image.complete);

    if (pending.length) {
      await Promise.race([
        Promise.all(
          pending.map(
            (image) =>
              new Promise((resolve) => {
                image.addEventListener('load', resolve, { once: true });
                image.addEventListener('error', resolve, { once: true });
              })
          )
        ),
        new Promise((resolve) => setTimeout(resolve, REVEAL_TIMEOUT_MS)),
      ]);
    }

    // Ein Frame Luft: `load` heisst dekodiert, nicht gemalt. Ohne die Pause
    // faellt der Schleier gelegentlich einen Frame zu frueh und es blitzt weiss.
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.removeAttribute('data-loading');
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
